import React, { FC } from 'react';
import {
  ActivityLogRecordChange,
  RecordChangeOriginalPost,
} from 'types/ActivityLog';
import { STATUS_LABEL } from '@kontentino/kontentino-constants/Posts';
import Link from 'components/shared/Link';
import { getBasePath } from 'utils/url';
import routes, { modalRoutes } from 'constants/routes';
import { ActivityRecordOptions } from 'constants/activityRecord';
import { PostPublishingMethod } from 'types/Post';

type Props = {
  change: ActivityLogRecordChange;
  groupType: string;
};

const {
  ACTIONS,
  ACTIONS_LABELS,
  ATTRIBUTE_LABELS,
  ATTRIBUTES,
  ATTRIBUTES_VALUES,
  GROUP_TYPES,
} = ActivityRecordOptions;

const PUBLISHING_METHOD_LABELS: Record<PostPublishingMethod, string> = {
  mobileApp: 'manual',
  direct: 'direct',
};

function isPublishingMethod(
  value: ActivityLogRecordChange['value'],
): value is PostPublishingMethod {
  return value === 'mobileApp' || value === 'direct';
}

const RecordSingleChange: FC<Props> = ({ change, groupType }) => {
  function getStatusLabel() {
    return typeof change.value === 'number'
      ? STATUS_LABEL[change.value] || ''
      : '';
  }

  function getActionLabel() {
    return (
      ACTIONS_LABELS[
        change.action.toUpperCase() as keyof typeof ACTIONS_LABELS
      ] || ''
    );
  }

  function getPostStatusActionMessage() {
    if (typeof change.value !== 'number') {
      return '';
    }

    const isManual = groupType === GROUP_TYPES.POST_UPDATE_MANUAL;

    if (isManual) {
      return `manually set status to ${getStatusLabel()}`;
    }

    return (
      ATTRIBUTE_LABELS.POST_STATUS[
        change.value as keyof typeof ATTRIBUTE_LABELS.POST_STATUS
      ] ?? ''
    );
  }

  function getChangeSubjectLabel() {
    return (
      ATTRIBUTE_LABELS[
        change.attribute.toUpperCase() as keyof typeof ATTRIBUTE_LABELS
      ] || ''
    );
  }

  function renderFormattedRecordSingleChange() {
    if (!ATTRIBUTES_VALUES.includes(change.attribute)) {
      return 'made unknown action';
    }

    if (change.attribute === ATTRIBUTES.POST_STATUS_MANUAL) {
      return (
        <>
          {ATTRIBUTE_LABELS.POST_STATUS_MANUAL} {getStatusLabel()}
        </>
      );
    }

    if (change.attribute === ATTRIBUTES.POST_CLONE_PARENT) {
      const post = change.value as RecordChangeOriginalPost;

      const originalPostLink = `${getBasePath()}${routes.POSTS_CALENDAR}?id=${
        post.post_id
      }#${modalRoutes.POST_DETAIL}`;

      return (
        <>
          original post:{' '}
          <Link
            to={originalPostLink}
            target="_blank"
            rel="noreferrer"
            className="!tw-underline"
          >
            here
          </Link>
        </>
      );
    }

    if (
      change.attribute === ATTRIBUTES.POST_STATUS &&
      change.action === ACTIONS.CHANGE
    ) {
      return groupType === GROUP_TYPES.CREATE
        ? `added status: ${getStatusLabel()}`
        : getPostStatusActionMessage();
    }

    if (
      change.attribute === ATTRIBUTES.PUBLISHING_METHOD &&
      change.action === ACTIONS.CHANGE &&
      isPublishingMethod(change.value)
    ) {
      const previousMethod: PostPublishingMethod =
        change.value === 'direct' ? 'mobileApp' : 'direct';

      return `${getActionLabel()} ${getChangeSubjectLabel()} from ${
        PUBLISHING_METHOD_LABELS[previousMethod]
      } to ${PUBLISHING_METHOD_LABELS[change.value]}`;
    }

    if (change.attribute === ATTRIBUTES.SHARE_TO_FEED) {
      return `${getActionLabel()} ${ATTRIBUTE_LABELS.SHARE_TO_FEED}`;
    }

    return (
      <>
        {getActionLabel()} {getChangeSubjectLabel()}
      </>
    );
  }

  return <>{renderFormattedRecordSingleChange()}</>;
};

export default RecordSingleChange;
